import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import React from "react";
import { useDrag, useDrop } from "react-dnd";
import { calculateHexColorBrightness } from "helpers/Helpers";
import { IProduct } from "redux/reducers/models/product";
import { ProductCardsItemTypes } from "../ProductCards";
import "./productCard.scss";

interface IProductCardProps {
  product: IProduct;
  isSelected: boolean;
  isPositionInASelectedRow: (position: number) => boolean;
  selectProduct: (event: React.MouseEvent<HTMLDivElement>, productId: number) => void;
  onRemoveProduct: (id: number) => void;
  onChangeProductPosition: (id: number, position: number) => void;
}

export interface IProductCardItem {
  id: number;
}

export default function ProductCard(props: IProductCardProps) {
  const { product, isSelected, isPositionInASelectedRow, selectProduct, onRemoveProduct, onChangeProductPosition } =
    props;

  const item: IProductCardItem = {
    id: product.id,
  };

  const [{ isDragging }, drag] = useDrag(() => {
    return {
      item: item,
      type: ProductCardsItemTypes.PRODUCT_CARD,
      collect: monitor => ({
        isDragging: !!monitor.isDragging(),
      }),
    };
  }, []);

  const [{ isOver, dropAllowed }, drop] = useDrop(
    () => ({
      accept: ProductCardsItemTypes.PRODUCT_CARD,
      canDrop: (dropped: IProductCardItem) => dropped.id !== product.id,
      drop: (dropped: IProductCardItem) => {
        if (product.folder_position === undefined || product.folder_position === null) {
          return;
        }
        onChangeProductPosition(dropped.id, product.folder_position);
      },
      collect: monitor => ({
        isOver: monitor.isOver({ shallow: true }),
        dropAllowed: monitor.canDrop(),
      }),
    }),
    [product.folder_position, product.id, onChangeProductPosition],
  );

  const showDropHighlight = isOver && dropAllowed;
  const selectedRow = isPositionInASelectedRow(product.folder_position);
  const backgroundColor = selectedRow ? "#0000FF" : product.meta?.button_color;
  const backgroundColorBrightness = backgroundColor ? calculateHexColorBrightness(backgroundColor) : null;
  const foregroundColor = backgroundColorBrightness === null ? "white" : backgroundColorBrightness < 50 ? "white" : "black";

  return (
    <div
      className={`product-card-container${isSelected ? " product-card-container--selected" : ""}${showDropHighlight ? " product-card-container--drop-target" : ""}`}
      ref={node => {
        drag(node);
        drop(node);
      }}
      style={isDragging ? { opacity: 0.55 } : undefined}
    >
      <div
        className="product-card"
        style={{
          ...(selectedRow ? { backgroundColor: "blue" } : product.meta?.button_color ? { backgroundColor: product.meta.button_color } : {}),
          color: foregroundColor,
          cursor: isDragging ? "grabbing" : "grab",
        }}
        onClick={e => selectProduct(e, product.id)}
      >
        <div>
          <div className="product-card-title">{product.preferred_title ? product.preferred_title : product.title}</div>
          <div className="product-card-vendor">{product.vendor_title}</div>
        </div>
        <FontAwesomeIcon
          onClick={() => onRemoveProduct(product.id)}
          className="product-card-remove-icon"
          icon={faXmark}
          style={{ color: foregroundColor }}
        />
      </div>
      {showDropHighlight ? <div className="product-card-drop-target-ring" aria-hidden /> : null}
    </div>
  );
}
